import { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";

// Constantes normativas 2026 (Perú)
const RMV_2026 = 1130;
const ASIGNACION_FAMILIAR = RMV_2026 * 0.10; 

const TASA_BONIF_ESSALUD = 0.09; 
const TASA_BONIF_EPS = 0.0675;

const PERIODOS = [
  { id: "julio", etiqueta: "Julio (periodo Ene – Jun)" },
  { id: "diciembre", etiqueta: "Diciembre (periodo Jul – Dic)" },
];

const REGIMENES = [
  { id: "general", nombre: "Régimen General", detalle: "Gratificación completa", factor: 1, sinGratificacion: false },
  { id: "pequena", nombre: "Pequeña Empresa", detalle: "Media gratificación", factor: 0.5, sinGratificacion: false },
  { id: "micro", nombre: "Microempresa", detalle: "Sin gratificación", factor: 0, sinGratificacion: true },
];

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function diasEntreFechas(fechaInicioStr, fechaFinStr) {
  if (!fechaInicioStr || !fechaFinStr) return 0;
  const [y1, m1, d1] = fechaInicioStr.split("-").map(Number);
  const [y2, m2, d2] = fechaFinStr.split("-").map(Number);
  if (!y1 || !y2) return 0;
  const dias = (y2 - y1) * 360 + (m2 - m1) * 30 + (d2 - d1) + 1;
  return Math.max(0, dias);
}

function evitarEnter(e) {
  if (e.key === "Enter") {
    e.preventDefault();
    e.currentTarget.blur();
  }
}

export default function CalculadoraGratificacion() {
  const [regimenId, setRegimenId] = useState("general");
  const [periodoId, setPeriodoId] = useState("julio");
  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [tieneHijos, setTieneHijos] = useState(false);
  const [seguro, setSeguro] = useState("essalud");

  const [modoTiempo, setModoTiempo] = useState("meses"); 
  const [mesesCompletos, setMesesCompletos] = useState(6);
  const [diasSueltos, setDiasSueltos] = useState(0);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  const [mostrarIngresos, setMostrarIngresos] = useState(false);
  const [comisiones, setComisiones] = useState(0);
  const [horasExtras, setHorasExtras] = useState(0);
  const [alimentacion, setAlimentacion] = useState(0);
  const [bonificaciones, setBonificaciones] = useState(0);

  const periodo = PERIODOS.find((p) => p.id === periodoId) ?? PERIODOS[0];
  const regimen = REGIMENES.find((r) => r.id === regimenId) ?? REGIMENES[0];

  const resultado = useMemo(() => {
    const bruto = Number(sueldoBruto) || 0;
    const asignacion = tieneHijos ? ASIGNACION_FAMILIAR : 0;

    const ingresosAdicionales =
      (Number(comisiones) || 0) +
      (Number(horasExtras) || 0) +
      (Number(alimentacion) || 0) +
      (Number(bonificaciones) || 0);

    const remuneracionComputable = bruto + asignacion + ingresosAdicionales;

    let diasBrutos = 0;
    if (modoTiempo === "meses") {
      const meses = Math.min(6, Math.max(0, Number(mesesCompletos) || 0));
      const dias = Math.max(0, Number(diasSueltos) || 0);
      diasBrutos = meses * 30 + dias;
    } else {
      diasBrutos = diasEntreFechas(fechaInicio, fechaFin);
    }
    const diasComputables = Math.min(180, Math.max(0, diasBrutos));

    const gratificacionBase = remuneracionComputable * (diasComputables / 180);
    const gratificacion = regimen.sinGratificacion ? 0 : gratificacionBase * regimen.factor;

    const tasaBonif = seguro === "essalud" ? TASA_BONIF_ESSALUD : TASA_BONIF_EPS;
    const bonificacionExtraordinaria = regimen.sinGratificacion ? 0 : gratificacion * tasaBonif;

    const total = gratificacion + bonificacionExtraordinaria;

    return {
      bruto,
      asignacion,
      ingresosAdicionales,
      remuneracionComputable,
      diasComputables,
      gratificacion,
      tasaBonif,
      bonificacionExtraordinaria,
      total,
    };
  }, [
    sueldoBruto,
    tieneHijos,
    seguro,
    modoTiempo,
    mesesCompletos,
    diasSueltos,
    fechaInicio,
    fechaFin,
    comisiones,
    horasExtras,
    alimentacion,
    bonificaciones,
    regimen,
  ]);

  const bloqueado = regimen.sinGratificacion;

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            🎁
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora de Gratificación
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Julio y diciembre + bonificación extraordinaria</p>
          </div>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Régimen laboral
              </label>
              <div className="grid grid-cols-3 gap-2">
                {REGIMENES.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRegimenId(r.id)}
                    className={`rounded-lg border py-2.5 px-1 text-center transition-colors ${
                      regimenId === r.id
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <span className="block text-xs font-semibold leading-tight">{r.nombre}</span>
                    <span className="block mt-0.5 text-[11px] text-slate-400">{r.detalle}</span>
                  </button>
                ))}
              </div>
            </div>

            {bloqueado && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3.5">
                <span className="text-amber-600">⚠️</span>
                <p className="text-xs leading-relaxed text-amber-800">
                  Los trabajadores del régimen de <strong>Microempresa</strong> (Ley N.° 30056) no
                  tienen derecho a gratificación por ley. El resto del formulario queda
                  deshabilitado.
                </p>
              </div>
            )}

            <div
              className={`space-y-5 transition-opacity ${
                bloqueado ? "opacity-40 pointer-events-none select-none" : ""
              }`}
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Periodo de pago
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIODOS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPeriodoId(p.id)}
                      className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${
                        periodoId === p.id
                          ? "border-red-700 bg-red-50 text-red-800"
                          : "border-slate-300 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {p.id === "julio" ? "Julio" : "Diciembre"}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-slate-400">{periodo.etiqueta}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Sueldo bruto mensual
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                    S/
                  </span>
                  <input
                    type="number"
                    min="0"
                    value={sueldoBruto}
                    onChange={(e) => setSueldoBruto(e.target.value)}
                    onKeyDown={evitarEnter}
                    className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-4 py-2.5 text-slate-900 font-mono text-lg focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                    placeholder="3000"
                  />
                </div>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={tieneHijos}
                  onChange={(e) => setTieneHijos(e.target.checked)}
                  className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                />
                <span className="text-sm text-slate-700">
                  Recibo asignación familiar ({formatearSoles(ASIGNACION_FAMILIAR)})
                </span>
              </label>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Seguro de salud
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSeguro("essalud")}
                    className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${
                      seguro === "essalud"
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    EsSalud (9%)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSeguro("eps")}
                    className={`rounded-lg border py-2.5 text-sm font-medium transition-colors ${
                      seguro === "eps"
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    EPS (6.75%)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Tiempo trabajado
                </label>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setModoTiempo("meses")}
                    className={`rounded-lg border py-2 text-sm font-medium transition-colors ${
                      modoTiempo === "meses"
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Por meses
                  </button>
                  <button
                    type="button"
                    onClick={() => setModoTiempo("fechas")}
                    className={`rounded-lg border py-2 text-sm font-medium transition-colors ${
                      modoTiempo === "fechas"
                        ? "border-red-700 bg-red-50 text-red-800"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Por fechas exactas
                  </button>
                </div>

                {modoTiempo === "meses" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Meses completos trabajados
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="6"
                        value={mesesCompletos}
                        onChange={(e) => setMesesCompletos(e.target.value)}
                        onKeyDown={evitarEnter}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                      <p className="mt-1 text-xs text-slate-400">de 0 a 6 (semestre completo)</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Días sueltos
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="29"
                        value={diasSueltos}
                        onChange={(e) => setDiasSueltos(e.target.value)}
                        onKeyDown={evitarEnter}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                      <p className="mt-1 text-xs text-slate-400">del mes incompleto</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Fecha de inicio
                      </label>
                      <input
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Fecha de fin
                      </label>
                      <input
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-400">
                      Se calcula con el método legal de meses de 30 días (semestre de 180 días),
                      con tope de 180 días.
                    </p>
                  </div>
                )}
              </div>

              <div className="pt-1 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setMostrarIngresos((v) => !v)}
                  className="flex items-center justify-between w-full mt-4 text-left"
                >
                  <span className="text-sm font-medium text-slate-700">
                    Ingresos adicionales (opcional)
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                      mostrarIngresos ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {mostrarIngresos && (
                  <div className="mt-3">
                    <p className="mb-3 text-xs leading-relaxed text-slate-400">
                      Solo incluye conceptos que recibes de manera regular (al menos 3 meses
                      consecutivos). Estos montos se suman a tu remuneración computable.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Comisiones (S/)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={comisiones}
                          onChange={(e) => setComisiones(e.target.value)}
                          onKeyDown={evitarEnter}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Horas Extras (S/)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={horasExtras}
                          onChange={(e) => setHorasExtras(e.target.value)}
                          onKeyDown={evitarEnter}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Alimentación (S/)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={alimentacion}
                          onChange={(e) => setAlimentacion(e.target.value)}
                          onKeyDown={evitarEnter}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">
                          Bonificaciones (S/)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={bonificaciones}
                          onChange={(e) => setBonificaciones(e.target.value)}
                          onKeyDown={evitarEnter}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Total a recibir
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.total))}
            </p>

            {bloqueado ? (
              <p className="text-sm text-slate-400">
                Régimen Microempresa: no corresponde gratificación por ley.
              </p>
            ) : (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Remuneración computable</span>
                  <span className="font-mono">{formatearSoles(resultado.remuneracionComputable)}</span>
                </div>
                {resultado.ingresosAdicionales > 0 && (
                  <div className="flex justify-between text-slate-400">
                    <span>&nbsp;&nbsp;incluye ingresos adicionales</span>
                    <span className="font-mono">{formatearSoles(resultado.ingresosAdicionales)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-300">
                  <span>Tiempo computable</span>
                  <span className="font-mono">{resultado.diasComputables} / 180 días</span>
                </div>
                <div className="flex justify-between text-slate-300 border-t border-slate-700 pt-1.5 mt-1.5">
                  <span>Gratificación</span>
                  <span className="font-mono">{formatearSoles(resultado.gratificacion)}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>+ Bonificación extraordinaria ({(resultado.tasaBonif * 100).toFixed(2)}%)</span>
                  <span className="font-mono">{formatearSoles(resultado.bonificacionExtraordinaria)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Régimen</span>
                  <span className="font-mono">{regimen.nombre}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-slate-400">
          Herramienta orientativa. La gratificación equivale a la remuneración
          computable por el tiempo trabajado en el semestre (máximo 6/6), ajustada
          según el régimen laboral (General: completa, Pequeña empresa: media
          gratificación, Microempresa: sin gratificación). La bonificación
          extraordinaria (Ley 30334) reemplaza el aporte a EsSalud o EPS que ya no
          paga el empleador, y no está sujeta a descuentos de AFP/ONP ni impuesto a la
          renta. No constituye asesoría legal, contable ni laboral.
        </p>
      </div>
    </div>
  );
}